import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { MapPin, School, Users, Star, Eye, MessageCircle, Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import StarRating from './StarRating';
import ReviewsModal from './ReviewsModal';
import LocationSelectionModal from './LocationSelectionModal';
import schoolStatsCache from '../utils/schoolStatsCache';
import studentCountCache from '../utils/studentCountCache';

// Helper function to get region color
const getRegionColor = (region) => {
    const regionColors = {
        'Region 1': '#3B82F6',
        'Region 2E': '#10B981',
        'Region 2W': '#F59E0B',
        'Region 3': '#8B5CF6',
        'Region 4': '#EF4444',
        'Region 5N': '#06B6D4',
        'Region 5S': '#84CC16',
        'Greater Banjul Area': '#3B82F6',
        'West Coast Region': '#10B981',
        'North Bank Region': '#F59E0B',
        'Central River Region': '#8B5CF6',
        'Upper River Region': '#EF4444',
        'Lower River Region': '#06B6D4'
    };
    return regionColors[region] || '#6B7280';
};

const GroupedSchoolCard = ({ schools, isSelected, onClick, onShowReview, refreshTrigger }) => {
    const [schoolStats, setSchoolStats] = useState({});
    const [loading, setLoading] = useState({});
    const [showReviewsModal, setShowReviewsModal] = useState(false);
    const [selectedSchoolForModal, setSelectedSchoolForModal] = useState(null);
    const [expanded, setExpanded] = useState(false);
    const [showLocationModal, setShowLocationModal] = useState(false);
    const [totalStudentCount, setTotalStudentCount] = useState(0);

    // Fetch stats for all schools in the group with throttling
    useEffect(() => {
        if (schools && schools.length > 0) {
            // Throttle requests to avoid 429 errors - increased delay
            schools.forEach((school, index) => {
                setTimeout(() => {
                    fetchSchoolStats(school.id);
                }, index * 2000); // Increased to 2 second delay between requests
            });
            fetchTotalStudentCount();
        }
    }, [schools?.map(s => s.id).join(',')]); // Only depend on school IDs to prevent unnecessary re-fetches

    // Refresh stats when refreshTrigger changes (after review submission)
    useEffect(() => {
        if (schools && schools.length > 0 && refreshTrigger > 0) {
            console.log('GroupedSchoolCard: Refreshing stats due to refreshTrigger:', refreshTrigger);
            // Clear cache and refetch stats
            schools.forEach((school, index) => {
                schoolStatsCache.clearSchool(school.id); // Clear cache
                setTimeout(() => {
                    fetchSchoolStats(school.id);
                }, index * 1000); // 1 second delay for refresh
            });
        }
    }, [refreshTrigger]);

    const fetchSchoolStats = async (schoolId) => {
        // Check cache first
        const cachedStats = schoolStatsCache.get(schoolId);
        if (cachedStats) {
            setSchoolStats(prev => ({ ...prev, [schoolId]: cachedStats }));
            return;
        }

        setLoading(prev => ({ ...prev, [schoolId]: true }));
        try {
            const response = await fetch(`/api/reviews/stats/${schoolId}`);
            if (response.ok) {
                const stats = await response.json();
                setSchoolStats(prev => ({ ...prev, [schoolId]: stats }));
                // Cache the result
                schoolStatsCache.set(schoolId, stats);
            } else if (response.status === 429) {
                console.warn(`Rate limited for school ${schoolId}, using default stats`);
                // Set default stats for rate limited requests
                const defaultStats = { averageRating: 0, reviewCount: 0, ratingDistribution: {} };
                setSchoolStats(prev => ({ ...prev, [schoolId]: defaultStats }));
            }
        } catch (error) {
            console.error('Error fetching school stats:', error);
            // Set default stats on error
            const defaultStats = { averageRating: 0, reviewCount: 0, ratingDistribution: {} };
            setSchoolStats(prev => ({ ...prev, [schoolId]: defaultStats }));
        } finally {
            setLoading(prev => ({ ...prev, [schoolId]: false }));
        }
    };

    const fetchTotalStudentCount = async () => {
        try {
            const counts = await studentCountCache.getStudentCounts();
            const total = schools.reduce((sum, school) => {
                return sum + studentCountCache.getStudentCount(school.id);
            }, 0);
            setTotalStudentCount(total);
        } catch (error) {
            console.error('Error fetching total student count:', error);
            setTotalStudentCount(0);
        }
    };

    const handleViewReviews = (e, school) => {
        if (e && typeof e.stopPropagation === 'function') {
            e.stopPropagation();
        }
        setSelectedSchoolForModal(school);
        setShowReviewsModal(true);
    };

    const handleWriteReview = (e, school) => {
        if (e && typeof e.stopPropagation === 'function') {
            e.stopPropagation();
        }
        console.log('GroupedSchoolCard: handleWriteReview called for school:', school?.name);
        console.log('GroupedSchoolCard: onShowReview function:', typeof onShowReview);
        console.log('GroupedSchoolCard: school object:', school);

        if (onShowReview && typeof onShowReview === 'function') {
            console.log('GroupedSchoolCard: Calling onShowReview with school:', school.name);
            console.log('GroupedSchoolCard: School object:', school);
            onShowReview(school);
            console.log('GroupedSchoolCard: onShowReview called successfully');
        } else {
            console.error('GroupedSchoolCard: onShowReview is not a function:', onShowReview);
        }
    };

    const handleCardClick = () => {
        if (hasMultipleSchools) {
            // For grouped schools, navigate to first school AND show modal
            const firstSchool = schools[0];
            if (onClick && firstSchool) {
                onClick(firstSchool); // Navigate map to first school
            }
            setShowLocationModal(true); // Then show location selection
        } else {
            // For single school, just navigate
            if (onClick) {
                onClick(schools[0]);
            }
        }
    };

    const handleLocationSelect = (school) => {
        if (onClick) {
            onClick(school);
        }
    };

    if (!schools || schools.length === 0) return null;

    const primarySchool = schools[0];
    const hasMultipleSchools = schools.length > 1;
    const totalReviews = Object.values(schoolStats).reduce((sum, stats) => sum + (stats?.reviewCount || 0), 0);
    const averageRating = Object.values(schoolStats).reduce((sum, stats) => sum + (stats?.averageRating || 0), 0) / schools.length;

    return (
        <>
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className={`bg-white rounded-none card-shadow border-2 transition-all duration-300 cursor-pointer hover:card-shadow-hover ${isSelected
                    ? 'border-primary-200 shadow-md bg-primary-50 school-card-expanded'
                    : 'border-gray-200 hover:border-primary-200'
                    } ${hasMultipleSchools ? 'border-orange-300 hover:border-orange-400' : ''}`}
                onClick={handleCardClick}
            >
                {/* Main Card Content */}
                <div className="p-4">
                    {/* Header with School Name and Region */}
                    <div className="flex items-start justify-between mb-3">
                        <div className="flex-1 min-w-0 pr-4">
                            <div className="flex items-center space-x-2 mb-1">
                                <div
                                    className="w-3 h-3 rounded-full flex-shrink-0"
                                    style={{ backgroundColor: getRegionColor(primarySchool.region) }}
                                />
                                <h3 className="font-bold text-lg text-gray-900 truncate flex-1" style={{ minWidth: 0, color: '#111827' }}>
                                    {primarySchool.name || 'Unknown School'}
                                </h3>
                                {hasMultipleSchools && (
                                    <div className="flex items-center space-x-1 text-orange-600">
                                        <span className="text-sm font-medium">({schools.length} locations)</span>
                                        {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                                    </div>
                                )}
                            </div>

                            {/* School Type Badge - directly under name */}
                            <div className="mb-2">
                                <span
                                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium text-white"
                                    style={{ backgroundColor: getRegionColor(primarySchool.region) }}
                                >
                                    <School size={12} className="mr-1" />
                                    {hasMultipleSchools ? 'Multiple Types' : primarySchool.type}
                                </span>
                            </div>

                            <div className="flex items-center space-x-1 text-sm text-gray-600 mb-2">
                                <MapPin size={14} />
                                <span className="truncate">
                                    {hasMultipleSchools
                                        ? `${schools.length} locations across ${new Set(schools.map(s => s.region)).size} regions`
                                        : `${primarySchool.district}, ${primarySchool.region}`
                                    }
                                </span>
                            </div>
                        </div>

                        {/* Location Selection Button for Multi-Location Schools */}
                        {hasMultipleSchools && (
                            <div className="flex-shrink-0">
                                <button
                                    onClick={(e) => {
                                        if (e && typeof e.stopPropagation === 'function') {
                                            e.stopPropagation();
                                        }
                                        setShowLocationModal(true);
                                    }}
                                    className="flex items-center space-x-1 px-3 py-1.5 text-sm bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200 transition-colors"
                                    title="Select Location"
                                >
                                    <MapPin size={14} />
                                    <span>Select Location</span>
                                </button>
                            </div>
                        )}
                    </div>

                    {/* School Details */}
                    <div className="space-y-2 mb-4">
                        <div className="flex items-center space-x-4 text-sm text-gray-600">
                            <div className="flex items-center space-x-1">
                                <Users size={14} />
                                <span>{primarySchool.management}</span>
                            </div>
                            {primarySchool.classification && (
                                <div className="flex items-center space-x-1">
                                    <span className="w-1 h-1 bg-gray-400 rounded-full"></span>
                                    <span>{primarySchool.classification}</span>
                                </div>
                            )}
                        </div>

                        {/* Total Student Count */}
                        <div className="flex items-center space-x-2 text-sm">
                            <div className="flex items-center space-x-1 text-primary-600">
                                <Users size={14} />
                                <span className="font-medium">{totalStudentCount} students enrolled across {schools.length} locations</span>
                            </div>
                        </div>

                        {primarySchool.address && (
                            <p className="text-sm text-gray-600 line-clamp-2">
                                {primarySchool.address}
                            </p>
                        )}
                    </div>

                    {/* Rating Section */}
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        transition={{ duration: 0.3 }}
                        className="border-t border-gray-200 pt-4"
                    >
                        {hasMultipleSchools ? (
                            <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-3">
                                        <div className="text-center">
                                            <div className="text-2xl font-bold text-gray-900">
                                                {averageRating > 0 ? averageRating.toFixed(1) : '0.0'}
                                            </div>
                                            <StarRating
                                                rating={averageRating}
                                                size={16}
                                                showCount={true}
                                                reviewCount={totalReviews}
                                            />
                                        </div>
                                    </div>

                                    {/* Action Buttons */}
                                    <div className="flex items-center space-x-2 action-buttons">
                                        {totalReviews > 0 && (
                                            <button
                                                onClick={(e) => handleViewReviews(e, primarySchool)}
                                                className="flex items-center space-x-1 px-3 py-1.5 text-sm btn-secondary rounded-lg"
                                            >
                                                <Eye size={14} />
                                                <span>View Reviews</span>
                                            </button>
                                        )}
                                        <button
                                            onClick={(e) => handleWriteReview(e, primarySchool)}
                                            className="flex items-center space-x-1 px-3 py-1.5 text-sm btn-review text-white rounded-lg"
                                        >
                                            <MessageCircle size={14} />
                                            <span>Write Review</span>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-3">
                                        <div className="text-center">
                                            <div className="text-2xl font-bold text-gray-900">
                                                {schoolStats[primarySchool.id]?.averageRating?.toFixed(1) || '0.0'}
                                            </div>
                                            <StarRating
                                                rating={schoolStats[primarySchool.id]?.averageRating || 0}
                                                size={16}
                                                showCount={true}
                                                reviewCount={schoolStats[primarySchool.id]?.reviewCount || 0}
                                            />
                                        </div>
                                    </div>

                                    {/* Action Buttons */}
                                    <div className="flex items-center space-x-2 action-buttons">
                                        {schoolStats[primarySchool.id]?.reviewCount > 0 && (
                                            <button
                                                onClick={(e) => handleViewReviews(e, primarySchool)}
                                                className="flex items-center space-x-1 px-3 py-1.5 text-sm btn-secondary rounded-lg"
                                            >
                                                <Eye size={14} />
                                                <span>View Reviews</span>
                                            </button>
                                        )}
                                        <button
                                            onClick={(e) => handleWriteReview(e, primarySchool)}
                                            className="flex items-center space-x-1 px-3 py-1.5 text-sm btn-review text-white rounded-lg"
                                        >
                                            <MessageCircle size={14} />
                                            <span>Write Review</span>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </motion.div>

                    {/* Expanded View for Multiple Schools */}
                    {hasMultipleSchools && expanded && (
                        <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.3 }}
                            className="border-t border-gray-200 pt-4 mt-4"
                        >
                            <h4 className="font-semibold text-gray-900 mb-3">All Locations:</h4>
                            <div className="space-y-3">
                                {schools.map((school, index) => (
                                    <div
                                        key={school.id}
                                        className="bg-gray-50 rounded-lg p-3 border border-orange-200"
                                        onClick={(e) => e.stopPropagation()} // Prevent parent click handler
                                    >
                                        <div className="flex items-start justify-between">
                                            <div className="flex-1">
                                                <div className="flex items-center space-x-2 mb-1">
                                                    <div
                                                        className="w-2 h-2 rounded-full flex-shrink-0"
                                                        style={{ backgroundColor: getRegionColor(school.region) }}
                                                    />
                                                    <span className="font-medium text-gray-900">{school.type}</span>
                                                </div>
                                                <div className="flex items-center space-x-1 text-sm text-gray-600 mb-2">
                                                    <MapPin size={12} />
                                                    <span>{school.district}, {school.region}</span>
                                                </div>
                                                {loading[school.id] ? (
                                                    <div className="flex items-center space-x-2">
                                                        <Loader2 size={14} className="animate-spin text-primary-600" />
                                                        <span className="text-sm text-gray-600">Loading reviews...</span>
                                                    </div>
                                                ) : schoolStats[school.id]?.reviewCount > 0 ? (
                                                    <div className="flex items-center space-x-2">
                                                        <StarRating
                                                            rating={schoolStats[school.id].averageRating}
                                                            size={12}
                                                            showCount={true}
                                                            reviewCount={schoolStats[school.id].reviewCount}
                                                        />
                                                    </div>
                                                ) : (
                                                    <span className="text-sm text-gray-500">No reviews yet</span>
                                                )}
                                            </div>
                                            <div className="flex items-center space-x-2" onClick={(e) => e.stopPropagation()}>
                                                {schoolStats[school.id]?.reviewCount > 0 && (
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleViewReviews(e, school);
                                                        }}
                                                        className="px-2 py-1 text-xs btn-secondary rounded hover:bg-gray-200 transition-colors"
                                                        title="View Reviews"
                                                    >
                                                        <Eye size={12} />
                                                    </button>
                                                )}
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleWriteReview(e, school);
                                                    }}
                                                    className="px-2 py-1 text-xs btn-review text-white rounded hover:opacity-90 transition-opacity"
                                                    title="Write Review"
                                                >
                                                    <MessageCircle size={12} />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </motion.div>
                    )}
                </div>
            </motion.div>

            {/* Reviews Modal */}
            <ReviewsModal
                school={selectedSchoolForModal}
                isOpen={showReviewsModal}
                onClose={() => {
                    setShowReviewsModal(false);
                    setSelectedSchoolForModal(null);
                }}
                refreshTrigger={refreshTrigger}
            />

            {/* Location Selection Modal */}
            <LocationSelectionModal
                isOpen={showLocationModal}
                onClose={() => setShowLocationModal(false)}
                schools={schools}
                onLocationSelect={handleLocationSelect}
                onShowReview={onShowReview}
                onViewReviews={handleViewReviews}
                schoolStats={schoolStats}
            />
        </>
    );
};

export default GroupedSchoolCard;
